import * as React from 'react'
import produce from 'immer'
import { Form, Input, Button, Spin, Tabs, Dropdown, Menu, Tooltip, message } from 'antd'
import { isNil, set, cloneDeep, isEqual, flow, get } from 'lodash'
import { withRouter } from 'react-router-dom'
import { showError, ValidationError } from 'helpers/errors'
import { allowPricing, getUserPermission, createFilterUserSuppliers } from 'helpers/auth'
import { formatDateTime } from 'helpers/dateTime'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  createDropdownRender,
  createSearchSuppliers,
  validateBarcode,
  isReadOnly,
} from 'helpers/formViews'
import { HARD_SPACE } from 'helpers/utils'
import { setStorageItem } from 'helpers/localStorage'
import { getKeyValueItem, setKeyValueItem } from 'helpers/keyValueStorage'
import linkTargets from 'options/linkTargets'
import { t } from 'helpers/i18n'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_SUBMIT_EVENT, FORM_CHANGE_EVENT } from 'options/events'
import { PRIVATE_DATABASE } from 'options/products'
import { RED } from 'options/colors'
import Modal from 'elements/Modal'
import Icon from 'elements/Icon'
import Select, { Option, getOptionProps } from 'elements/Select'
import InputNumber from 'elements/InputNumber'
import DatePicker from 'elements/DatePicker'
import SelectProducts from 'containers/Products/Select'
import Documents from 'containers/Documents/ChildListView'
import TransferInventory from 'containers/Inventory/Transfer'
import Drawer from 'elements/Drawer'
import FormButtons from 'elements/FormButtons'
import Checkbox from 'elements/Checkbox'
import Comments from 'elements/Comments'
import InputImage from 'elements/InputImage'
import Help from 'elements/Help'
import { Row, Col } from 'elements/Grid'
import { getCountryName } from 'helpers/countries'

const AssetsFormView = linkTargets.assetRecord.formComponent
const ProductsFormView = linkTargets.productRecord.formComponent
const OrdersFormView = linkTargets.orderRecord.formComponent

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.searchSuppliers = createSearchSuppliers(this)
  }

  componentDidMount() {
    const { selectedProduct } = this.props

    if (selectedProduct) {
      this.setState({ selectedProduct }, this.handleSelectProductOk)
    } else {
      this.fetchItem()
    }

    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  handleFormChange = () => this.setState({ formHasChanged: true })

  handleSelectProductOk = async () => {
    const { selectedProduct, selectedProductFilterDto } = this.state

    if (selectedProduct) {
      const {
        barcode,
        itemNumber,
        manufacturer,
        description: itemDescription,
        price,
        packageSizeUom,
        preferredSupplierName: itemSupplierName,
        reworkTypeDescription,
      } = selectedProduct

      const catalogTableName = selectedProductFilterDto?.catalogTableName ?? PRIVATE_DATABASE

      this.setState({ selectProductVisible: false })

      try {
        if (catalogTableName !== PRIVATE_DATABASE && selectedProduct) {
          await this.props
            .copyToPrivate({
              catalogProductIds: [selectedProduct.id],
              catalogTableName,
            })
            .catch(() => {})
        }

        const responses = await Promise.all([
          this.props.newItem({
            barcode,
            itemNumber,
            manufacturer,
            itemDescription,
            price,
            packageSizeUom,
            itemSupplierName,
            reworkTypeDescription,
            supplierId: 0,
            onHand: 0,
            onHand2: 0,
            min: 0,
            max: 0,
            averageUsed: 0,
            averageOrdered: 0,
            doNotReorder: this.props.customer.generalSettings.autoDoNotReorder,
          }),
          this.props.getSettings({ type: this.props.settingsType }),
          this.props.getLocations(),
          this.props.getSuppliers(),
        ])

        const image = selectedProduct.id
          ? await this.props
              .getDocumentContents({
                request: {
                  domainObjectId: selectedProduct.id,
                  domainObjectType: 'Product',
                  documentType: 'ObjectImage',
                  documentName: selectedProduct.barcode,
                },
              })
              .catch(() => {})
          : null

        const documentItems = selectedProduct.id
          ? await this.props
              .getDocumentItems({
                request: {
                  domainObjectId: selectedProduct.id,
                  domainObjectType: 'Product',
                  documentType: 'ObjectDocument',
                },
              })
              .catch(() => {})
          : null

        this.setState({
          item: cloneDeep(responses[0].value.data),
          itemOriginal: cloneDeep(responses[0].value.data),
          documentItems: get(documentItems, 'value.data.items', []),
          image: cloneDeep(image?.value?.data),
          imageOriginal: cloneDeep(image?.value?.data),
          fieldSettings: responses[1].value.data.fieldSettings,
          locations: get(responses[2], 'value.data.items', []),
          suppliers: responses[3].value.data,
          formHasChanged: false,
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.inventoryId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    if (isNil(itemId) && !this.props.linkTargetRecord) {
      this.setState({ selectProductVisible: true })
    } else {
      try {
        const responses = await Promise.all([
          this.props.getItem(itemId).catch((error) => {
            this.props.onCancel?.()
            throw error
          }),
          this.props.getSettings({ type: this.props.settingsType }),
          this.props.getLocations(),
        ])

        const item = cloneDeep(responses[0].value.data)

        const suppliers = await this.props.getSuppliers(
          item.supplierId ? { supplierIds: [item.supplierId] } : undefined
        )

        const image =
          item.id && item.productId
            ? await this.props
                .getDocumentContents({
                  request: {
                    domainObjectId: item.productId,
                    domainObjectType: 'Product',
                    documentType: 'ObjectImage',
                    documentName: item.barcode,
                  },
                })
                .catch(() => {})
            : null

        const documentItems =
          item.id && item.productId
            ? await this.props
                .getDocumentItems({
                  request: {
                    domainObjectId: item.productId,
                    domainObjectType: 'Product',
                    documentType: 'ObjectDocument',
                  },
                })
                .catch(() => {})
            : null

        this.setState({
          item,
          documentItems: get(documentItems, 'value.data.items', []),
          itemOriginal: cloneDeep(item),
          fieldSettings: responses[1].value.data.fieldSettings,
          locations: get(responses[2], 'value.data.items', []),
          suppliers: suppliers?.value?.data ?? {},
          image: cloneDeep(image?.value?.data),
          imageOriginal: cloneDeep(image?.value?.data),
          formHasChanged: false,
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  handleActionsMenuClick = async ({ key }) => {
    switch (key) {
      case 'issueThisItem':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          try {
            setStorageItem('inventory.issue.formView', {
              quickscan: true,
              requireQuantity: true,
            })
            await getKeyValueItem('inventory.issue.sideForm')
            await setKeyValueItem('inventory.issue.sideForm', {
              displayName: this.state.item.displayName,
              locationId: this.state.item.locationId,
              operatorId: this.props.user.operatorId,
              operatorBarcode: this.props.user.operatorBarcode,
              operatorDisplayName: this.props.user.operatorName,
            })
            this.props.history.push('/inventory/issue')
            this.props.onCancel()
          } catch (error) {
            showError({ error })
          }
        }
        break

      case 'orderThisItem':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          try {
            setStorageItem('orders.formView', {
              quickscan: true,
              requireQuantity: true,
            })
            await getKeyValueItem('orders.formView.sideForm')
            await setKeyValueItem('orders.formView.sideForm', {
              displayName: this.state.item.displayName,
              locationId: this.state.item.locationId,
              operatorId: this.props.user.operatorId,
              operatorBarcode: this.props.user.operatorBarcode,
              operatorDisplayName: this.props.user.operatorName,
            })
            this.setState({ orderDrawerVisible: true, orderDrawerSaving: false })
          } catch (error) {
            showError({ error })
          }
        }
        break

      case 'transferInventory':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.setState({ transferInventoryVisible: true })
        }
        break

      case 'createAssetForItem':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          try {
            const { item } = this.state
            const response = await this.props.generateAssetId({
              description: item.itemDescription,
              locationId: item.locationId,
              manufacturer: item.manufacturer,
              replacementBarcode: item.barcode,
              itemNumber: item.itemNumber,
            })

            this.setState({
              assetDrawerVisible: true,
              assetDrawerSaving: false,
              assetDrawerLinkTargetRecord: { ...response.value.data, assetId: response.value.data.id },
            })
          } catch (error) {
            showError({ error })
          }
        }
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  setItemValue = (name, value) => {
    if (getUserPermission('Inventory') !== 'Edit') {
      return
    }

    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (draft.item.min === 0 && draft.item.max === 0 && this.props.customer.generalSettings.autoDoNotReorder) {
          draft.item.doNotReorder = true
        }

        this.props.form.setFieldsValue({ [name]: value })
      })
    )
  }

  hasUnsavedChanges = () =>
    this.state.formHasChanged || !this.state.item?.id || !isEqual(this.state.item, this.state.itemOriginal)

  render() {
    const { form, onCancel, customer, tenantTypeIsTrms, tenantGroupIsCardinal } = this.props
    const { item, selectedProduct, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return (
        <>
          <Spin />
          <Modal
            title={t('selectProduct')}
            visible={this.state.selectProductVisible}
            okText={t('select')}
            onOk={this.handleSelectProductOk}
            okButtonProps={{ disabled: !selectedProduct }}
            onCancel={onCancel}
            width={992}
          >
            <SelectProducts
              onSelect={(values) => this.setState({ selectedProduct: values[0] })}
              onFilter={(filterDto) => this.setState({ selectedProductFilterDto: filterDto })}
            />
          </Modal>
        </>
      )
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this) || getUserPermission('Inventory') !== 'Edit'

    return (
      <>
        <Form layout="vertical" onSubmit={readOnly ? stopEvent : this.handleSubmit} colon={false}>
          {item.id ? (
            <Dropdown
              overlay={
                <Menu onClick={this.handleActionsMenuClick}>
                  <Menu.Item key="orderThisItem">{t('orderThisItem')}</Menu.Item>
                  <Menu.Item key="issueThisItem" disabled={item.lockedForCycleCount}>
                    {t('issueThisItem')}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item key="createAssetForItem">{t('createAssetForItem')}</Menu.Item>
                  <Menu.Divider />
                  <Menu.Item key="transferInventory" disabled={readOnly || item.lockedForCycleCount}>
                    {t('transferInventory')}
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button className="form-items-actions">
                {t('actions')}
                <Icon type="KeyboardArrowDown" />
              </Button>
            </Dropdown>
          ) : null}
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col md={8} xs={24}>
                <Form.Item label={t('barcode')}>
                  {['View', 'Edit'].includes(getUserPermission('Products')) && (
                    <div className="tofino-label-buttons">
                      <Tooltip title={t(getUserPermission('Products') === 'Edit' ? 'editProduct' : 'viewProduct')}>
                        <Button
                          size="small"
                          onClick={() => {
                            if (this.hasUnsavedChanges()) {
                              message.error(t('saveChangesFirst'))
                            } else {
                              this.setState({
                                productDrawerVisible: true,
                                productDrawerSaving: false,
                              })
                            }
                          }}
                        >
                          <Icon type="Edit" />
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                  <Input value={item.barcode} disabled />
                </Form.Item>
              </Col>
              <Col md={16} xs={24}>
                <Form.Item label={createLabel('itemDescription')}>
                  <Input value={item.itemDescription} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Tabs
              activeKey={this.state.tabsActiveKey || 'inventory'}
              onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
            >
              <Tabs.TabPane key="inventory" tab={t('inventory')} forceRender>
                <Row>
                  <Col md={16} xs={24}>
                    <Row>
                      <Col md={12} xs={24}>
                        {item.id ? (
                          <Form.Item label={createLabel('locationId')}>
                            <Input value={item.locationName} placeholder={t('none')} disabled />
                          </Form.Item>
                        ) : (
                          <Form.Item label={createLabel('locationId')}>
                            {createFieldDecorator('locationId')(
                              <Select onChange={(value) => this.setItemValue('locationId', value)}>
                                {(this.state.locations ?? []).map((each) => (
                                  <Option key={each.id} value={each.id}>
                                    <span {...getOptionProps(each)}>{each.displayName}</span>
                                  </Option>
                                ))}
                              </Select>
                            )}
                          </Form.Item>
                        )}
                        <Form.Item label={createLabel('binLocation')}>
                          {createFieldDecorator('binLocation')(<Input readOnly={readOnly} />)}
                        </Form.Item>
                        {tenantTypeIsTrms && (
                          <Form.Item label={createLabel('supplierId')}>
                            {createFieldDecorator('supplierId')(
                              <Select
                                dropdownRender={createDropdownRender(this.state.suppliers)}
                                onChange={(value) => this.setItemValue('supplierId', value)}
                                onSearch={this.searchSuppliers}
                                readOnly={readOnly || !item.canEditPreferredSupplier}
                                showSearch
                              >
                                <Option value={0}>{t('notAssigned')}</Option>
                                {(this.state.suppliers?.items ?? [])
                                  .filter((each) => each.id)
                                  .filter(createFilterUserSuppliers(item.supplierId))
                                  .map((each) => (
                                    <Option key={each.id} value={each.id}>
                                      <span {...getOptionProps(each)}>{each.name}</span>
                                    </Option>
                                  ))}
                              </Select>
                            )}
                          </Form.Item>
                        )}
                        <Form.Item label={createLabel('accountNumber')}>
                          {createFieldDecorator('accountNumber')(<Input readOnly={readOnly} />)}
                        </Form.Item>
                      </Col>
                      <Col md={12} xs={24}>
                        {['onHand', 'onHand2'].map((each) => (
                          <Form.Item key={each} label={createLabel(each)}>
                            {item.lockedForCycleCount ? (
                              <Input
                                value={item[each]}
                                suffix={
                                  <Tooltip title={t('onhandQuantitiesLockedForCycleCount')}>
                                    <Icon type="Lock" color={RED} />
                                  </Tooltip>
                                }
                                disabled
                              />
                            ) : (
                              createFieldDecorator(each)(<InputNumber readOnly={readOnly} min={0} />)
                            )}
                          </Form.Item>
                        ))}
                        <Form.Item label={createLabel('min')}>
                          {createFieldDecorator('min', {
                            extraRules: [
                              {
                                validator: (rule, value, callback) => {
                                  callback(
                                    value > form.getFieldValue('max') ? [new Error(t('minValidationError'))] : []
                                  )
                                },
                              },
                            ],
                          })(
                            <InputNumber
                              onChange={(value) => this.setItemValue('min', value)}
                              readOnly={readOnly}
                              min={0}
                            />
                          )}
                        </Form.Item>
                        <Form.Item label={createLabel('max')}>
                          {createFieldDecorator('max', {
                            extraRules: [
                              {
                                validator: (rule, value, callback) => {
                                  callback(
                                    value < form.getFieldValue('min') ? [new Error(t('maxValidationError'))] : []
                                  )
                                },
                              },
                            ],
                          })(
                            <InputNumber
                              onChange={(value) => this.setItemValue('max', value)}
                              readOnly={readOnly}
                              min={0}
                            />
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item label={createLabel('description')}>
                      {createFieldDecorator('description')(<Input.TextArea rows={4} readOnly={readOnly} />)}
                    </Form.Item>
                    <Comments
                      value={item.customerComment}
                      onChange={(value) => this.setItemValue('customerComment', value)}
                      rows={5}
                      label={createLabel('customerComment')}
                      title={t('addComment')}
                      readOnly={readOnly}
                    />
                    <Form.Item label={createLabel('rowCreated')}>
                      <Input value={formatDateTime(item.rowCreated, { showTime: true })} disabled />
                    </Form.Item>
                  </Col>
                  <Col md={8} xs={24}>
                    <Form.Item label={createLabel('averageUsed')}>
                      <Input value={item.averageUsed} disabled />
                    </Form.Item>
                    <Form.Item label={createLabel('averageOrdered')}>
                      <Input value={item.averageOrdered} disabled />
                    </Form.Item>
                    <Form.Item label={createLabel('lastReceivedDate')}>
                      <DatePicker value={item.lastReceivedDate} placeholder={t('none')} readOnly={readOnly} disabled />
                    </Form.Item>
                    {fieldSettings.find((one) => one.dtoFieldName === 'inventoryBarcode') && (
                      <Form.Item label={createLabel('inventoryBarcode')}>
                        {createFieldDecorator('inventoryBarcode', {
                          extraRules: [validateBarcode()],
                        })(
                          <Input
                            onChange={(e) => this.setItemValue('inventoryBarcode', e.target.value)}
                            readOnly={readOnly}
                          />
                        )}
                      </Form.Item>
                    )}
                    <Form.Item label={HARD_SPACE}>
                      {createFieldDecorator('doNotReorder', { valuePropName: 'checked' })(
                        <Checkbox
                          disabled={item.min === 0 && item.max === 0 && customer.generalSettings.autoDoNotReorder}
                          readOnly={readOnly}
                        >
                          {createLabel('doNotReorder')}
                        </Checkbox>
                      )}
                    </Form.Item>
                    <Form.Item label=" ">
                      {createFieldDecorator('overrideQuantity', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly}>{createLabel('overrideQuantity')}</Checkbox>
                      )}
                    </Form.Item>
                    <Form.Item label=" ">
                      {createFieldDecorator('setUsageEqualOrder', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly}>{createLabel('setUsageEqualOrder')}</Checkbox>
                      )}
                    </Form.Item>
                    <Form.Item label=" ">
                      {createFieldDecorator('criticalItem', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly}>{createLabel('criticalItem')}</Checkbox>
                      )}
                    </Form.Item>
                    <Form.Item label=" ">
                      {createFieldDecorator('mustCount', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly} disabled={tenantGroupIsCardinal && item.cartType === 'REQ'}>
                          {createLabel('mustCount')}
                        </Checkbox>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane key="item" tab={t('item')} forceRender>
                <Row>
                  <Col md={16} xs={24}>
                    <Row>
                      <Col md={12} xs={24}>
                        <Form.Item label={createLabel('barcode')}>
                          <Input value={item.barcode} disabled />
                        </Form.Item>
                        <Form.Item label={createLabel('itemNumber')}>
                          <Input value={item.itemNumber} disabled />
                        </Form.Item>
                        <Form.Item label={createLabel('originCountryCode')}>
                          <Input value={getCountryName(item.originCountryCode, this.props.locale)} disabled />
                        </Form.Item>
                        <Form.Item label={createLabel('packageSizeUom')}>
                          <Input value={item.packageSizeUom} disabled />
                        </Form.Item>
                      </Col>
                      <Col md={12} xs={24}>
                        {tenantTypeIsTrms && (
                          <Form.Item label={createLabel('itemSupplierName')}>
                            <Input value={item.itemSupplierName} disabled />
                          </Form.Item>
                        )}
                        <Form.Item label={createLabel('manufacturer')}>
                          <Input value={item.manufacturer} disabled />
                        </Form.Item>
                        <Form.Item label={createLabel('expires')}>
                          <Input value={item.expiresDescription} disabled />
                        </Form.Item>
                        {allowPricing() && (
                          <Form.Item label={createLabel('price')}>
                            <InputNumber value={item.price} currency disabled />
                          </Form.Item>
                        )}
                        {!tenantTypeIsTrms && (
                          <Form.Item label={createLabel('reworkTypeDescription')}>
                            <Input value={item.reworkTypeDescription} disabled />
                          </Form.Item>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Form.Item label={createLabel('itemDescription')}>
                          <Input.TextArea value={item.itemDescription} rows={4} disabled />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                  <Col md={8} xs={24}>
                    {tenantTypeIsTrms && (
                      <Form.Item label={createLabel('reworkTypeDescription')}>
                        <Input value={item.reworkTypeDescription} disabled />
                      </Form.Item>
                    )}
                    <InputImage
                      label={t('productImage')}
                      value={this.state.image}
                      height={tenantTypeIsTrms ? '281px' : '373px'}
                      showButtons={false}
                    />
                  </Col>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane
                key="documents"
                tab={
                  <>
                    {t('documents')} <Help title={t('productDocuments')} />
                  </>
                }
                forceRender
              >
                <Documents
                  domainObjectId={item.productId}
                  domainObjectType="Product"
                  items={this.state.documentItems ?? []}
                  pageTotals={this.state.documentItemsTotals ?? {}}
                  onChange={(values) => this.setState({ documentItems: values })}
                  readOnly
                />
              </Tabs.TabPane>
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Modal
          title={`${t('transferInventory')} - ${this.state.item.barcode}`}
          visible={this.state.transferInventoryVisible}
          onCancel={() => this.setState({ transferInventoryVisible: false })}
          okText={t('transfer')}
          onOk={() => Emitter.emit(FORM_SUBMIT_EVENT)}
          okButtonProps={{ loading: this.state.transferInventoryLoading }}
          width={750}
        >
          <TransferInventory
            parentRecord={item}
            onSave={(pending) =>
              this.promiseState({ transferInventoryLoading: pending }).then(async () => {
                await this.props.onSave?.(pending)

                if (!pending) {
                  await this.fetchItem()
                }
              })
            }
            onSaveAndClose={() => this.setState({ transferInventoryVisible: false })}
            onCancel={() => this.setState({ transferInventoryVisible: false })}
          />
        </Modal>
        <Drawer
          title={`${t(linkTargets.assetRecord.drawerTitleLanguageKey)} - ${
            this.state.assetDrawerLinkTargetRecord?.barcode
          }`}
          size={linkTargets.assetRecord.formSize}
          visible={this.state.assetDrawerVisible}
          onClose={() => this.setState({ assetDrawerVisible: false })}
          saving={this.state.assetDrawerSaving}
        >
          <AssetsFormView
            linkTargetRecord={this.state.assetDrawerLinkTargetRecord}
            onSave={(pending) =>
              this.promiseState({ assetDrawerSaving: pending }).then(async () => {
                await this.props.onSave?.(pending)

                if (!pending) {
                  await this.fetchItem()
                }
              })
            }
            onSaveAndClose={() => this.setState({ assetDrawerVisible: false })}
            onCancel={() => this.setState({ assetDrawerVisible: false })}
          />
        </Drawer>
        <Drawer
          title={`${t('product')} - ${item.barcode}`}
          size={linkTargets.productRecord.formSize}
          visible={this.state.productDrawerVisible}
          onClose={() => this.setState({ productDrawerVisible: false })}
          saving={this.state.productDrawerSaving}
        >
          <ProductsFormView
            linkTargetRecord={item ? { productId: item.productId } : undefined}
            onSave={(pending) =>
              this.promiseState({ productDrawerSaving: pending }).then(async () => {
                await this.props.onSave?.(pending)

                if (!pending) {
                  await this.fetchItem()
                }
              })
            }
            onSaveAndClose={() => this.setState({ productDrawerVisible: false })}
            onCancel={() => this.setState({ productDrawerVisible: false })}
            readOnly={getUserPermission('Products') !== 'Edit'}
          />
        </Drawer>
        <Drawer
          title={t('createOrder')}
          size={linkTargets.orderRecord.formSize}
          visible={this.state.orderDrawerVisible}
          onClose={() => this.setState({ orderDrawerVisible: false })}
          saving={this.state.orderDrawerSaving}
        >
          <OrdersFormView
            onSave={(pending) =>
              this.promiseState({ orderDrawerSaving: pending }).then(async () => {
                await this.props.onSave?.(pending)

                if (!pending) {
                  await this.fetchItem()
                }
              })
            }
            onSaveAndClose={() => this.setState({ orderDrawerVisible: false })}
            onCancel={() => this.setState({ orderDrawerVisible: false })}
          />
        </Drawer>
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
